import { useEffect, useMemo, useState } from "react";
import {
  FaBullhorn,
  FaChartBar,
  FaComments,
  FaPaperPlane,
  FaQrcode,
  FaRedoAlt,
  FaUsers,
} from "react-icons/fa";
import { FiArrowUpRight } from "react-icons/fi";
import { Sidebar } from "../components/Note";
import api from "../api";
import { ACCESS_TOKEN } from "../constants";
import "../styles/tailwind.output.css";

const RANGE_OPTIONS = [
  { value: "day", label: "Hoy" },
  { value: "week", label: "Esta semana" },
  { value: "month", label: "Este mes" },
  { value: "year", label: "Este año" },
];

const DEMO_BARS = [
  { day: "Lun", sent: 12, replies: 4 },
  { day: "Mar", sent: 23, replies: 9 },
  { day: "Mie", sent: 18, replies: 6 },
  { day: "Jue", sent: 31, replies: 12 },
  { day: "Vie", sent: 27, replies: 9 },
  { day: "Sab", sent: 9, replies: 3 },
  { day: "Dom", sent: 6, replies: 2 },
];

function Resumen() {
  const [username, setUsername] = useState("");
  const [range, setRange] = useState("day");
  const [metrics, setMetrics] = useState({
    campaigns: { count: 0, clients_total: 0, sends_total: 0 },
    contacts: { total: 0 },
    replies: { total: 0, examples: [] },
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({
    is_connected: false,
    mode: "unknown",
  });
  const [dark, setDark] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("theme") === "dark";
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (dark) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [dark]);

  useEffect(() => {
    const storedUsername = localStorage.getItem("username");
    if (storedUsername) setUsername(storedUsername);

    const token = localStorage.getItem(ACCESS_TOKEN);
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const u = payload?.username || payload?.user || payload?.user_id || payload?.sub;
        if (u) setUsername((prev) => prev || u);
      } catch {}
    }

    api
      .get("me/")
      .then((res) => {
        const d = res?.data || {};
        setIsAdmin(!!(d.is_staff || d.is_superuser));
      })
      .catch(() => setIsAdmin(false));
  }, []);

  const fetchMetrics = async (r = range) => {
    try {
      setRefreshing(true);
      const res = await api.get("metrics/dashboard/", { params: { range: r } });
      if (res?.data) setMetrics(res.data);
    } catch {
      // Silencioso: mantener ultimo estado renderizado
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMetrics(range);
  }, [range]);

  useEffect(() => {
    const delay = range === "day" ? 3000 : 5000;
    const id = setInterval(() => fetchMetrics(range), delay);
    return () => clearInterval(id);
  }, [range]);

  useEffect(() => {
    let cancelled = false;
    const loadStatus = async () => {
      try {
        const { data } = await api.get("integration/connection-status/");
        if (!cancelled) {
          setConnectionStatus({
            is_connected: !!data?.is_connected,
            mode: String(data?.mode || "unknown"),
          });
        }
      } catch {
        if (!cancelled) {
          setConnectionStatus({ is_connected: false, mode: "unknown" });
        }
      }
    };

    loadStatus();
    const id = setInterval(loadStatus, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const handleResetToday = async () => {
    try {
      await api.post("metrics/reset-today/");
      await fetchMetrics(range);
      alert("Se reiniciaron los contadores de hoy.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error al reiniciar";
      alert(msg);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("lead-forms/");
        if (!cancelled && Array.isArray(data)) {
          const total = data.reduce(
            (acc, f) => acc + (typeof f.new_count === "number" ? f.new_count : 0),
            0
          );
          setNewLeadsCount(total);
        }
      } catch {
        // noop
      }
    };

    load();
    const id = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const greeting = useMemo(() => {
    return !username ? "Hola" : `Hola ${username},`;
  }, [username]);

  const rangeLabel = useMemo(() => {
    const map = {
      day: "el día",
      week: "la semana",
      month: "el mes",
      year: "el año",
    };
    return map[range] || "el periodo";
  }, [range]);

  const numberFmt = useMemo(() => new Intl.NumberFormat("es-EC"), []);

  const whatsappConnected = !!connectionStatus.is_connected;

  const repliesSubtitle = useMemo(() => {
    const raw = Array.isArray(metrics?.replies?.examples) ? metrics.replies.examples : [];
    const phones = raw
      .map((x) => (typeof x === "string" ? x : x?.from || x?.phone || x?.number || ""))
      .filter(Boolean);

    if (phones.length === 0) return "Ultimas respuestas recibidas";
    if (phones.length === 1) return `Ultima: ${phones[0]}`;
    return `Ultimas: ${phones.slice(0, 3).join(", ")}`;
  }, [metrics]);

  const activityItems = useMemo(() => {
    const raw = Array.isArray(metrics?.replies?.examples) ? metrics.replies.examples : [];
    const fromReplies = raw.slice(0, 3).map((item, idx) => {
      const phone = typeof item === "string" ? item : item?.from || item?.phone || "Desconocido";
      const text = typeof item === "string" ? "Respondio al mensaje" : (item?.text || "Respondio al mensaje");
      let when = "Reciente";
      try {
        const d = new Date(item?.at);
        if (!Number.isNaN(d.getTime())) when = d.toLocaleString();
      } catch {
        when = "Reciente";
      }
      return {
        id: `reply-${idx}`,
        type: "response",
        description: `${phone}: ${text}`,
        time: when,
      };
    });

    if (fromReplies.length > 0) return fromReplies;

    return [
      {
        id: "fallback-1",
        type: "contact",
        description: "Sin actividad reciente en respuestas",
        time: "Ahora",
      },
    ];
  }, [metrics]);

  const barsData = useMemo(() => {
    const sends = Number(metrics?.campaigns?.sends_total || 0);
    const replies = Number(metrics?.replies?.total || 0);
    const contacts = Number(metrics?.contacts?.total || 0);
    const seed = sends + replies + contacts;

    if (seed === 0) return DEMO_BARS;

    return DEMO_BARS.map((d, idx) => {
      const factor = 0.6 + ((seed + idx * 13) % 7) / 10;
      return {
        day: d.day,
        sent: Math.max(1, Math.round(d.sent * factor)),
        replies: Math.max(1, Math.round(d.replies * factor)),
      };
    });
  }, [metrics]);

  const primaryCards = useMemo(
    () => [
      {
        key: "campaigns",
        title: "Campañas enviadas",
        value: numberFmt.format(metrics.campaigns?.count || 0),
        subtitle: `${numberFmt.format(metrics.campaigns?.clients_total || 0)} contactos en campañas`,
        cardClass:
          "border-emerald-500/35 bg-gradient-to-br from-[#1f8f4d] via-[#28b764] to-[#30d67a]",
        iconBg: "bg-white/16",
        iconColor: "text-white/90",
        valueClass: "text-white",
        titleClass: "text-white",
        subtitleClass: "text-white/80",
        badgeClass: "bg-white/20 text-white",
        Icon: FaBullhorn,
      },
      {
        key: "contacts",
        title: "Clientes registrados",
        value: numberFmt.format(metrics.contacts?.total || 0),
        subtitle: "Total de clientes registrados",
        cardClass:
          "border-violet-500/35 bg-gradient-to-br from-[#4a3a9d] via-[#6354b7] to-[#7465cb]",
        iconBg: "bg-white/16",
        iconColor: "text-white/90",
        valueClass: "text-white",
        titleClass: "text-white",
        subtitleClass: "text-white/80",
        badgeClass: "bg-white/20 text-white",
        Icon: FaUsers,
      },
      {
        key: "replies",
        title: "Respuestas de clientes",
        value: numberFmt.format(metrics.replies?.total || 0),
        subtitle: repliesSubtitle,
        cardClass:
          "border-orange-500/35 bg-gradient-to-br from-[#a05713] via-[#d46a0c] to-[#ef7d10]",
        iconBg: "bg-white/16",
        iconColor: "text-white/90",
        valueClass: "text-white",
        titleClass: "text-white",
        subtitleClass: "text-white/80",
        badgeClass: "bg-white/20 text-white",
        Icon: FaComments,
      },
    ],
    [metrics, numberFmt, repliesSubtitle]
  );

  const secondaryCards = useMemo(
    () => [
      {
        key: "qr",
        title: "Contactos por QR",
        value: numberFmt.format(metrics.contacts?.qr_imported || 0),
        subtitle: `Importados hoy desde formularios publicos (+${numberFmt.format(newLeadsCount)}).`,
        cardClass:
          "border-sky-500/35 bg-gradient-to-br from-[#1566a8] via-[#2387c4] to-[#2ea0da]",
        iconBg: "bg-white/16",
        iconColor: "text-white/90",
        valueClass: "text-white",
        titleClass: "text-white",
        subtitleClass: "text-white/80",
        badgeClass: "bg-white/20 text-white",
        Icon: FaQrcode,
      },
      {
        key: "sends",
        title: "Total de envíos",
        value: numberFmt.format(metrics.campaigns?.sends_total || 0),
        subtitle: `Envíos en ${rangeLabel}`,
        cardClass:
          "border-cyan-500/35 bg-gradient-to-br from-[#1f7f8c] via-[#2e98a8] to-[#3fb5c7]",
        iconBg: "bg-white/16",
        iconColor: "text-white/90",
        valueClass: "text-white",
        titleClass: "text-white",
        subtitleClass: "text-white/80",
        badgeClass: "bg-white/20 text-white",
        Icon: FaPaperPlane,
      },
    ],
    [metrics, newLeadsCount, numberFmt, rangeLabel]
  );

  const DashboardCard = ({
    title,
    value,
    subtitle,
    cardClass,
    iconBg,
    iconColor,
    valueClass,
    titleClass,
    subtitleClass,
    badgeClass,
    Icon,
  }) => (
    <article
      className={`group rounded-2xl border p-5 shadow-sm transition-transform duration-200 hover:-translate-y-0.5 min-h-[156px] ${cardClass}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className={`rounded-xl p-2.5 ${iconBg}`}>
          <Icon className={`text-sm ${iconColor}`} />
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badgeClass}`}>
            {RANGE_OPTIONS.find((opt) => opt.value === range)?.label || "Periodo"}
          </span>
          <FiArrowUpRight className="text-slate-500 text-base dark:text-slate-400" />
        </div>
      </div>

      <div className="mt-4">
        <div>
          <p className={`text-[44px] font-extrabold leading-none tracking-tight ${valueClass}`}>{value}</p>
          <p className={`mt-1 text-[33px] font-semibold leading-tight ${titleClass}`}>{title}</p>
          <p className={`mt-1 text-[13px] ${subtitleClass}`}>{subtitle}</p>
        </div>
      </div>
    </article>
  );

  return (
    <div className="ws-page ws-page-resumen flex min-h-screen transition-colors duration-300">
      <Sidebar />

      <main className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
        <section className="ws-header-surface rounded-xl border px-6 py-5 shadow-sm">
          <h1 className="text-[48px] font-bold tracking-tight ws-title">Resumen</h1>
          <p className="mt-1 text-[16px] ws-muted">Panorama general de tu actividad en campañas de WhatsApp...</p>
        </section>

        <section className="ws-greeting rounded-2xl border px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-[#061220] text-sm font-bold">
                {String(username || "WS").slice(0, 2).toUpperCase()}
              </span>
              <div>
                <p className="text-[36px] font-semibold ws-title">¡Hola, {username || "usuario"}!</p>
                <p className="text-sm ws-muted">Nos encanta tenerte interactuando con tus clientes</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setDark((v) => !v)}
                className="inline-flex items-center gap-2 rounded-full ws-chip px-3 py-2 text-xs font-semibold transition"
              >
                {dark ? "Claro" : "Oscuro"}
              </button>

              {isAdmin && (
                <button
                  onClick={handleResetToday}
                  className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-rose-700"
                >
                  <FaRedoAlt className={refreshing ? "animate-spin" : ""} />
                  Reiniciar hoy
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="font-semibold text-2xl ws-title">Resumen del período</h2>
          <div className="inline-flex items-center gap-1 rounded-xl p-1 ws-chip">
            {RANGE_OPTIONS.map((opt) => {
              const active = range === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setRange(opt.value)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                    active
                      ? "bg-[#23d767] text-[#04131f] shadow"
                      : "ws-muted hover:bg-[rgba(88,166,255,0.12)]"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {primaryCards.map((card) => (
            <DashboardCard key={card.key} {...card} />
          ))}
        </section>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-2">
          {secondaryCards.map((card) => (
            <DashboardCard key={card.key} {...card} />
          ))}
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="ws-panel lg:col-span-2 rounded-xl border p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-sm">Actividad de mensajes</h3>
                <p className="text-xs mt-0.5 ws-muted">Enviados vs respondidos en {rangeLabel}</p>
              </div>
              <FaChartBar className="text-cyan-500" />
            </div>

            <div className="h-[180px] flex items-end gap-2">
              {barsData.map((item) => {
                const sentHeight = Math.max(8, item.sent * 3);
                const replyHeight = Math.max(6, item.replies * 3);
                return (
                  <div key={item.day} className="flex-1 min-w-0">
                    <div className="h-[150px] flex items-end justify-center gap-1">
                      <div
                        className="w-2 rounded-t"
                        style={{ height: `${Math.min(sentHeight, 145)}px`, background: "#25d366" }}
                      />
                      <div
                        className="w-2 rounded-t"
                        style={{ height: `${Math.min(replyHeight, 145)}px`, background: "#58a6ff" }}
                      />
                    </div>
                    <p className="text-[11px] mt-1 text-center ws-muted">{item.day}</p>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#25d366]" />
                <span className="text-xs ws-muted">Enviados</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#58a6ff]" />
                <span className="text-xs ws-muted">Respondidos</span>
              </div>
            </div>
          </div>

          <div className="ws-panel rounded-xl border p-5">
            <h3 className="font-semibold text-sm mb-4">Actividad reciente</h3>
            <div className="space-y-3">
              {activityItems.map((item) => (
                <div key={item.id} className="flex gap-3">
                  <div
                    className="flex items-center justify-center rounded-full shrink-0 mt-0.5"
                    style={{
                      width: "28px",
                      height: "28px",
                      background: item.type === "response" ? "rgba(88,166,255,0.15)" : "rgba(188,140,255,0.15)",
                    }}
                  >
                    {item.type === "response" ? <FaComments className="text-[#58a6ff] text-xs" /> : <FaUsers className="text-[#bc8cff] text-xs" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs leading-snug">{item.description}</p>
                    <p className="text-[11px] mt-0.5 ws-muted">{item.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {Array.isArray(metrics?.replies?.examples) && metrics.replies.examples.length > 0 && (
          <section className="ws-panel rounded-xl border p-4">
            <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide">
                {range === "day" ? "Ultimas respuestas (hoy)" : "Ultimas respuestas"}
              </h3>
              <span className="text-xs ws-muted">
                {numberFmt.format(metrics.replies.total || 0)} en {rangeLabel}
              </span>
            </div>

            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {metrics.replies.examples.slice(0, 6).map((m, idx) => {
                const phone = typeof m === "string" ? m : m?.from || "Desconocido";
                let when = "";
                try {
                  const d = new Date(m?.at);
                  when = Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
                } catch {
                  when = "";
                }
                const text = (m?.text || "").trim();

                return (
                  <li
                    key={`reply-${idx}`}
                    className="rounded-lg border ws-reply-item p-3"
                  >
                    <p className="text-sm font-semibold">{phone}</p>
                    <p className="text-[11px] ws-muted">{when || "Sin marca de tiempo"}</p>
                    {text && <p className="mt-1 text-sm">{text}</p>}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <section
          className="rounded-lg px-4 py-3 text-xs border"
          style={{
            background: dark ? "rgba(88,166,255,0.12)" : "rgba(88,166,255,0.08)",
            borderColor: dark ? "rgba(88,166,255,0.32)" : "rgba(88,166,255,0.22)",
          }}
        >
          <span className="text-[#58a6ff]">Info: </span>
          Conteo = primeras respuestas unicas atribuidas a campanas (max. 1 por numero por corrida en el rango seleccionado).
        </section>
      </main>
    </div>
  );
}

export default Resumen;
